import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ChevronLeft, Brain, Palette, Zap, Home, Lightbulb,
  type LucideIcon,
} from 'lucide-react'

// ─── Section / step data ──────────────────────────────────────────────────────
//
// Status-color section (Green / Amber / Red / Learning) keeps its functional
// dots — those are *what the page is explaining*. Everything else uses neutral
// numbered indicators so the page reads as editorial copy, not a sticker book.

type StatusTone = 'forest' | 'sand' | 'critical' | 'muted'

interface Step {
  title:  string
  body:   string
  status?: StatusTone   // when present, render a colored dot instead of a number
}

interface Section {
  Icon:  LucideIcon
  title: React.ReactNode  // includes italic-last-word
  steps: Step[]
}

const sections: Section[] = [
  {
    Icon:  Brain,
    title: <>Pantry learns your <em className="italic">habits</em></>,
    steps: [
      { title: 'You add items',           body: 'Add the things you regularly buy — milk, eggs, bread. Just the basics to start.' },
      { title: 'You confirm restocks',    body: 'Every time you go shopping, swipe through your pantry in "Unpack" and confirm what you bought. This is how Pantry learns.' },
      { title: 'Pantry spots your rhythm',body: "After 3–5 restocks of an item, Pantry calculates how often you buy it and starts predicting when you'll run out." },
      { title: 'Your list writes itself', body: 'Items turn amber then red as you get close to running out. Your shopping list auto-generates based on these predictions.' },
    ],
  },
  {
    Icon:  Palette,
    title: <>What the colors <em className="italic">mean</em></>,
    steps: [
      { title: 'Green — Stocked',     body: 'You bought this recently and should be good for a while.',                                  status: 'forest'   },
      { title: 'Amber — Buy soon',    body: "Based on your history, you're getting close to running out. Pantry adds it to your list.", status: 'sand'     },
      { title: 'Red — Need now',      body: "You're likely out or very close. High priority on your shopping list.",                    status: 'critical' },
      { title: 'Learning — New item', body: "Pantry doesn't have enough data yet. Keep restocking and it'll figure out your pattern.", status: 'muted'    },
    ],
  },
  {
    Icon:  Zap,
    title: <>Quick <em className="italic">actions</em></>,
    steps: [
      { title: 'Long press any item',   body: 'Hold down on an item to get quick actions — mark as out, mark as used, just bought, or remove.' },
      { title: 'Start shopping mode',   body: 'On the list page, tap "Start shopping" to check items off as you go. All household members see updates in real time.' },
      { title: 'After you shop',        body: 'Tap "Unpack" and swipe through your items. Swipe right = got it, swipe left = skip. This is the most important habit for accurate predictions.' },
      { title: 'Scan shortcuts',        body: 'Use the barcode scanner to add items instantly, or the receipt scanner to confirm everything you bought at once.' },
    ],
  },
  {
    Icon:  Home,
    title: <>Households &amp; <em className="italic">sharing</em></>,
    steps: [
      { title: 'Invite household members', body: "Settings → Household → Invite someone. They'll join your shared pantry and see everything in real time." },
      { title: 'Shop together',            body: 'Multiple people can check off items from the same list simultaneously. Great for splitting up the shopping.' },
      { title: 'Item notes',               body: 'Add notes to items — "get the organic one" or "from Trader Joe\'s only". All household members see them.' },
    ],
  },
  {
    Icon:  Lightbulb,
    title: <>Tips for best <em className="italic">results</em></>,
    steps: [
      { title: 'Unpack after every shop',  body: 'This single habit is what makes Pantry smart. Even a quick two-minute swipe-through teaches the app your rhythm.' },
      { title: 'Add expiry dates',         body: 'For fresh items like meat, dairy, and produce — adding an expiry date lets Pantry warn you before things go bad.' },
      { title: 'Log prices when you shop', body: "In shopping mode, tap an item to expand and log the price. Over time you'll see spending trends and price alerts." },
      { title: 'Give it 2–3 shops',        body: "Pantry gets meaningfully smarter after just 2–3 shopping trips. The first week is a little manual — it's worth it." },
    ],
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusDotClass(tone: StatusTone): string {
  switch (tone) {
    case 'forest':   return 'bg-forest'
    case 'sand':     return 'bg-sand'
    case 'critical': return 'bg-critical'
    case 'muted':    return 'bg-ink-muted'
  }
}

// ─── HowItWorks ───────────────────────────────────────────────────────────────

export default function HowItWorks() {
  const navigate = useNavigate()

  return (
    <main className="min-h-screen bg-cream pb-16">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1.5 rounded-full text-ink"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={2} />
          </button>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Welcome
            </p>
            <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-0.5">
              How Pantry <em className="italic">works</em>
            </h1>
          </div>
        </div>

        {/* Hero */}
        <div className="bg-paper border border-border-sand rounded-3xl p-6 text-center mb-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            Your household memory
          </p>
          <h2 className="font-serif text-[20px] text-ink leading-[1.15] tracking-[-0.015em]">
            The more you use it, the <em className="italic">smarter</em> it gets.
          </h2>
          <p className="font-serif text-[13px] italic text-ink-muted mt-3 leading-[1.5] max-w-[300px] mx-auto">
            Pantry tracks what you have, learns when you run out, and builds your shopping list automatically.
          </p>
        </div>

        {/* Sections */}
        <div className="flex flex-col gap-4">
          {sections.map((section, si) => (
            <motion.div
              key={si}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: si * 0.05 }}
              className="bg-paper border border-border-sand rounded-3xl overflow-hidden"
            >
              {/* Section header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-border-hairline bg-cream-warm/40">
                <div className="h-9 w-9 rounded-[10px] bg-paper border border-border-sand flex items-center justify-center text-forest">
                  <section.Icon className="h-[18px] w-[18px]" strokeWidth={1.7} />
                </div>
                <h3 className="font-serif text-[16px] text-ink leading-tight">
                  {section.title}
                </h3>
              </div>

              {/* Steps */}
              <div className="divide-y divide-border-hairline">
                {section.steps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3 px-5 py-3.5">
                    {step.status ? (
                      <span
                        className={`h-2.5 w-2.5 rounded-full flex-shrink-0 mt-1.5 ${statusDotClass(step.status)}`}
                        aria-hidden
                      />
                    ) : (
                      <span className="font-serif italic text-[13px] text-ink-muted flex-shrink-0 w-5 mt-0.5 tabular-nums">
                        {i + 1}.
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-[14px] font-medium text-ink leading-tight">
                        {step.title}
                      </p>
                      <p className="font-serif text-[12px] italic text-ink-muted mt-1 leading-[1.5]">
                        {step.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Closing CTA */}
        <div className="bg-paper border border-border-sand rounded-3xl px-5 py-6 text-center mt-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
            Ready when you are
          </p>
          <h3 className="font-serif text-[18px] text-ink leading-[1.2]">
            Add your first <em className="italic">item</em>.
          </h3>
          <p className="font-serif text-[12px] italic text-ink-muted mt-2 leading-[1.5] max-w-[260px] mx-auto">
            Pantry takes it from there. The first week is a little manual — it's worth it.
          </p>
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="mt-4 inline-flex rounded-2xl bg-forest text-forest-ink px-5 py-2.5 text-[13px] font-bold tracking-[-0.01em]"
          >
            Go to my pantry
          </button>
        </div>

      </div>
    </main>
  )
}
